import {
  AccountLevel,
  AccountType,
  AgencyVerificationStatus,
} from "@/enums/agencies";
import { ScriptType } from "@/enums/campaigns";
import { MAX_CUSTOMER_CONCURRENCY } from "@/constants/concurrency";
import { AgencyRestrictions } from "@/types/agencies";
import { Org } from "@/types/org";

export const DEFAULT_LARGE_RESTRICTION = 1000000;
export const ENTERPRISE_MAX_ALLOWED_CONCURRENCY = 2000;

/** Default outbound campaign/script types when an org has no explicit list. */
export const DEFAULT_CAMPAIGN_SCRIPT_TYPES: ScriptType[] = [
  ScriptType.Phone,
  ScriptType.TextBlaster,
];

// Utility function to convert camelCase to underscore_case
const camelToUnderscore = (str: string): string => {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase();
};

// Utility function to convert script types to the correct format
const normalizeScriptType = (type: string): ScriptType => {
  // If it's already in the correct format, return it
  if (Object.values(ScriptType).includes(type as ScriptType)) {
    return type as ScriptType;
  }
  // Convert camelCase to underscore_case
  const normalized = camelToUnderscore(type);
  // Find the matching ScriptType enum value
  return (
    Object.values(ScriptType).find((st) => st === normalized) ||
    ScriptType.Phone
  ); // Default to Phone if no match found
};

const AGENCY_RESTRICTIONS_TRIAL: AgencyRestrictions = {
  maxAllowedConcurrency: MAX_CUSTOMER_CONCURRENCY,
  campaign: {
    maxTotal: 2,
    maxConcurrency: 5,
    maxContacts: 10,
    types: [...DEFAULT_CAMPAIGN_SCRIPT_TYPES],
  },
  contacts: {
    maxTotal: 100,
    maxUpload: 10000,
  },
  contactLists: {
    maxTotal: 2,
  },
  sequences: {
    maxTotal: 2,
  },
  redialingRules: {
    maxTotal: 2,
  },
  account: {
    level: AccountLevel.SMB,
    type: [AccountType.Insurance],
  },
  filesUpload: {
    maxTotal: 5,
  },
};

const AGENCY_RESTRICTIONS_PAID: AgencyRestrictions = {
  maxAllowedConcurrency: MAX_CUSTOMER_CONCURRENCY,
  campaign: {
    maxTotal: -1,
    maxConcurrency: 5,
    maxContacts: 100,
    types: [...DEFAULT_CAMPAIGN_SCRIPT_TYPES],
  },
  contacts: {
    maxTotal: 100000,
    maxUpload: 10000,
  },
  contactLists: {
    maxTotal: -1,
  },
  sequences: {
    maxTotal: 20,
  },
  redialingRules: {
    maxTotal: 20,
  },
  account: {
    level: AccountLevel.SMB,
    type: [AccountType.Insurance],
  },
  filesUpload: {
    maxTotal: 20,
  },
};

const AGENCY_RESTRICTIONS_VERIFIED: AgencyRestrictions = {
  maxAllowedConcurrency: MAX_CUSTOMER_CONCURRENCY,
  campaign: {
    maxTotal: -1,
    maxConcurrency: 5,
    maxContacts: 1000,
    types: [...DEFAULT_CAMPAIGN_SCRIPT_TYPES],
  },
  contacts: {
    maxTotal: 1000000,
    maxUpload: 20000,
  },
  contactLists: {
    maxTotal: -1,
  },
  sequences: {
    maxTotal: 100,
  },
  redialingRules: {
    maxTotal: 100,
  },
  account: {
    level: AccountLevel.SMB,
    type: [AccountType.Insurance],
  },
  filesUpload: {
    maxTotal: 20,
  },
};

export const GetRestrictions = (
  org: Org | null,
  allowOverride = true
): AgencyRestrictions => {
  if (org && org.details?.restrictions && allowOverride) {
    let restrictions = org.details.restrictions as AgencyRestrictions;

    if (restrictions.account === undefined) {
      restrictions.account = {
        level: AccountLevel.SMB,
        type: [AccountType.Insurance],
      };
    }

    if (restrictions.maxAllowedConcurrency === undefined) {
      restrictions.maxAllowedConcurrency =
        restrictions.account.level === AccountLevel.Enterprise
          ? ENTERPRISE_MAX_ALLOWED_CONCURRENCY
          : MAX_CUSTOMER_CONCURRENCY;
    }

    if (restrictions.campaign === undefined) {
      restrictions.campaign = {
        maxTotal: org.services?.stripeCustomerId ? -1 : 2,
        maxConcurrency: 5,
        maxContacts: org.services?.stripeCustomerId ? 100 : 10,
        types: [...DEFAULT_CAMPAIGN_SCRIPT_TYPES],
      };
    }

    if (restrictions.contacts === undefined) {
      restrictions.contacts = {
        maxTotal: org.services?.stripeCustomerId ? 100000 : 100,
        // Previously: org.services?.stripeCustomerId ? 10000 : 10000
        // Conditional returned the same value regardless of outcome
        maxUpload: 10000,
      };
    }

    if (restrictions.contactLists === undefined) {
      restrictions.contactLists = {
        maxTotal: org.services?.stripeCustomerId ? -1 : 2,
      };
    }

    if (restrictions.campaign.types === undefined) {
      restrictions.campaign.types = [...DEFAULT_CAMPAIGN_SCRIPT_TYPES];
    } else {
      // Convert any camelCase types to the correct format
      restrictions.campaign.types =
        restrictions.campaign.types.map(normalizeScriptType);
    }

    if (restrictions.sequences === undefined) {
      restrictions.sequences = {
        maxTotal: org.services?.stripeCustomerId ? 5 : 2,
      };
    }

    if (restrictions.redialingRules === undefined) {
      restrictions.redialingRules = {
        maxTotal: org.services?.stripeCustomerId ? 5 : 2,
      };
    }

    if (restrictions.filesUpload === undefined) {
      restrictions.filesUpload = {
        maxTotal: org.services?.stripeCustomerId ? 20 : 5,
      };
    }

    if (restrictions.account.level === AccountLevel.Enterprise) {
      restrictions.filesUpload = {
        maxTotal: 100,
      };
    }

    return restrictions;
  }

  let defaultRestrictions = AGENCY_RESTRICTIONS_TRIAL;

  if (org && (!!org.services?.stripeCustomerId || org.isDemo)) {
    defaultRestrictions =
      org.details?.agencyVerificationStatus ===
        AgencyVerificationStatus.Verified || org.isDemo
        ? AGENCY_RESTRICTIONS_VERIFIED
        : AGENCY_RESTRICTIONS_PAID;
  }

  return defaultRestrictions;
};

export const GetRestrictionText = (
  restriction: number,
  defaultMax?: string
): string | number => {
  return restriction >= 0
    ? restriction
    : defaultMax || DEFAULT_LARGE_RESTRICTION;
};

export const GetRestrictionValue = (restriction: number): number => {
  return restriction >= 0 ? restriction : DEFAULT_LARGE_RESTRICTION;
};
